import { Link } from '../routing';
import { useIsMobile } from '../utils/useIsMobile';
import { STATUS_TEXT_COLOR } from '../utils/targetBar';
import Icon from './Icon';

// The one amber callout style on /analysis — used by WeeklyBudgetBreakdown's
// unmapped-spend line and RevenueVsBudgetPanel's coverage banner.
//
// It exists because the two had quietly drifted: 10px/14px padding vs
// 8px/12px, marginTop 12 vs 14, #fff7ed vs #fef7ec — small enough that
// neither author noticed, big enough that side by side the page read as two
// different products. One component is what keeps them from drifting again.
//
// NO leading glyph. It carried a heart rather than a warning triangle to keep
// the tone casual, and was dropped entirely on 2026-09-18 (owner) — the amber
// wash already says "worth finishing", so the icon was decoration competing
// with the message for the narrowest part of a phone row.
//
// Text/icon color matches the target card's warning amber — same TEXT step
// (STATUS_TEXT_COLOR.ahead, #B45309), not the bar-fill step (#D97706): that
// fill color is 3.2:1 on white, fine for a bar (AA non-text is 3:1) but below
// AA's 4.5:1 for the 12.5-14px/500 weight this component sets, which is not
// "large text" under WCAG. Using the fill hex here would look matched and
// fail accessibility at the same time — the text step keeps both true.
const AMBER_TEXT = STATUS_TEXT_COLOR.ahead;
const btnStyle = {
  background: 'transparent', border: '1px solid var(--warning-line)', color: AMBER_TEXT,
  borderRadius: 8, fontWeight: 600, display: 'inline-flex', alignItems: 'center',
  justifyContent: 'center', textDecoration: 'none', cursor: 'pointer', flexShrink: 0,
  padding: '8px 14px', minHeight: 44, fontSize: 13.5,
};

const rowStyle = (compact) => ({
  display: 'flex', alignItems: 'center', gap: compact ? 8 : 10, flexWrap: 'wrap',
  background: 'var(--warning-bg)', borderRadius: 8,
  padding: compact ? '9px 12px' : '10px 14px', marginTop: compact ? 10 : 14,
  fontSize: compact ? 12.5 : 14, fontWeight: 500, color: AMBER_TEXT,
});

// The one amber callout style on /analysis.
//
// DESKTOP: message on the left, an outlined button on the right.
//
// MOBILE: the WHOLE strip is the control. A bordered pill squeezed onto the end
// of a wrapping sentence was the complaint — it reads as an afterthought, and a
// 34px pill (the previous compromise, taken to stop the strip becoming the
// tallest thing in the card) is a mean tap target on the surface these cards
// are mostly read on. Promoting the strip itself keeps the row exactly ONE line
// tall — the thing that compromise was protecting — while handing the whole
// card width to the thumb, with the label and a chevron left as the affordance.
export default function AmberCallout({ children, actionLabel, onAction, actionTo }) {
  const compact = useIsMobile(640);

  if (compact && actionLabel) {
    const inner = (
      <>
        <span style={{ flex: '1 1 auto', textAlign: 'start' }}>{children}</span>
        {/* marginInlineStart:auto so a message long enough to wrap still leaves
            the action at the END of its line, rather than parked under the
            start of the sentence where it reads as a stray third fragment. */}
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, fontWeight: 600, flexShrink: 0, marginInlineStart: 'auto' }}>
          {actionLabel}
          {/* Rotated the same way לצפייה rotates its chevron — in RTL that
              points at where the tap leads. */}
          <Icon name="chevron" size={14} style={{ transform: 'rotate(90deg)' }} />
        </span>
      </>
    );
    const tapStyle = {
      ...rowStyle(true), minHeight: 48, width: '100%', boxSizing: 'border-box',
      cursor: 'pointer', textDecoration: 'none', color: AMBER_TEXT,
    };
    return actionTo
      ? <Link to={actionTo} style={tapStyle}>{inner}</Link>
      : (
        <button
          type="button"
          onClick={onAction}
          // fontFamily, never the `font` SHORTHAND: `font: inherit` resets
          // size, weight and line-height too, so it silently wiped the 12.5px/500
          // this row shares with its <Link> twin and rendered the button variant
          // at the inherited 16px/400 — the same strip in two type scales
          // depending on whether it navigates or calls a handler.
          style={{ ...tapStyle, border: 'none', fontFamily: 'inherit', textAlign: 'start' }}
        >
          {inner}
        </button>
      );
  }

  return (
    <div style={rowStyle(compact)}>
      <span style={{ flex: '1 1 auto' }}>{children}</span>
      {actionLabel && (actionTo
        ? <Link to={actionTo} style={btnStyle}>{actionLabel}</Link>
        : <button type="button" onClick={onAction} style={btnStyle}>{actionLabel}</button>)}
    </div>
  );
}
